import { FileSearchResults } from "@/components/features/files/FileSearchResults";
import { useFileDuplication } from "@/components/features/files/duplication/FileDuplicationProvider";
import { useFileSearch } from "@/hooks/use-file-search";
import {
  canDownloadFile,
  canOfferFileDuplication,
  mergeFileSearchResults,
} from "@/lib/data/requests/files/file-adapter";
import { FileVersionTitle } from "@/components/features/files/FileVersionTitle";
import { downloadFileLink } from "@/lib/utils/matterFile";
import {
  downloadFile,
  downloadFileVersion,
} from "@/lib/data/requests/files/canonical-files";
import {
  ActionConfirmationModal,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  MatterPreviewHoverCard,
  ScrollHint,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useShareFolderTreeData } from "@/hooks/matter-view-models/use-matter-share-folder-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { searchContactsQueryOptions } from "@/lib/data/react-query";
import { cn } from "@/lib/utils/cn";
import {
  useCreateMatterFile,
  useDeleteMatterFile,
  useUpdateMatterFile,
} from "@/lib/data/react-query/matters/matter-file";
import {
  useCreateMatterShareFolderFile,
  useDeleteMatterShareFolder,
  useDeleteMatterShareFolderFile,
} from "@/lib/data/react-query/matters/matter-share-folder";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { acceptedFileMimeTypes } from "@/lib/data/static/fileStatics";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Matter,
  type MatterFile,
  type MatterShareFolderFile,
  type MassTort,
} from "@/lib/types";
import {
  getFileIcon,
  getFileNameParts,
  getFileMoveConflictMessage,
  getFileDocumentFormat,
  handleDownload,
  isMergeSupportedMatterFile,
} from "@/lib/utils/matterFile";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useQuery } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { uniq } from "lodash";
import {
  Download,
  EllipsisVertical,
  File,
  FilesIcon,
  FileText,
  Folder,
  FolderInput,
  Pencil,
  Plus,
  Share2,
  Trash2,
  WandSparkles,
  X,
} from "lucide-react";
import type { DragEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { CreateDocxFileSlideover } from "./CreateDocxFileSlideover";
import { EditDocxFileSlideover } from "./EditDocxFileSlideover";
import { FileRepositoryPdfEditorSlideover } from "./FileRepositoryPdfEditorSlideover";
import { type FileSortKey } from "./FileRepositorySort";
import { downloadRepositoryItems } from "./fileRepositoryDownload";
import { getSelectedRepositoryRoots } from "./fileRepositorySelection";
import { FileViewer, type FilePreviewVersion } from "./FileViewer";
import { FolderTree } from "./FolderTree";
import { MatterFileContextMenu } from "../context-menus/MatterFileContextMenu";
import { SelectDocumentModal } from "./signature-requests/SelectDocumentModal";

interface FileRepositoryProps {
  matter?: Matter;
  massTort?: MassTort;
  overRideFileSelectFn?: (itemId: string) => void;
  overRideFolderSelectFn?: (folderId: string) => void;
  onExternalSelectionChange?: (
    fileIds: string[],
    checked: boolean,
    replaceSelection?: boolean,
  ) => void;
  selectedFiles?: MatterFile[];
  enabledFileTypes?: string[];
  showMergeDocuments?: boolean;
  showShareFolders?: boolean;
  maxSelection?: number;
  excludedFileIds?: Set<string>;
  autoOpenNewFileUpload?: boolean;
  afterCreateFile?: (file: MatterFile) => Promise<void> | void;
  useExternalSelection?: boolean;
  showSelectionActions?: boolean;
  enableTreeDrag?: boolean;
  allowFileSelection?: boolean;
  showCheckboxes?: boolean;
  onSelectRoot?: () => void;
  compactPreviewLayout?: boolean;
  selectedFileId?: string;
}

type RegularNode = MatterFile & {
  kind?: "regular";
};
type ShareFolderNode = MatterFile &
  MatterShareFolderFile & { kind?: "share-folder" };
export type RepoItem = RegularNode | ShareFolderNode;
type SidebarTab = "folders" | "shared-folders";
type FastApiMatterFile = {
  description?: string | null;
  id?: string;
  idLocation?: string | null;
  isFolder?: boolean;
  massTortId?: string | null;
  matterId?: string | null;
  name?: string;
  presignedUrlKey?: string | null;
  variables?: string[] | null;
};

const toRepoMatterFile = (
  file: FastApiMatterFile,
  fallbackMatterId: string,
): MatterFile | null => {
  const id = file.id;
  const name = file.name;

  if (!id || !name) {
    return null;
  }

  return {
    ...file,
    matterId: file.matterId ?? fallbackMatterId,
    id,
    idLocation: file.idLocation ?? "",
    description: file.description ?? "",
    name,
  } as MatterFile;
};

export function FileRepository({
  matter,
  massTort,
  overRideFileSelectFn,
  overRideFolderSelectFn,
  onExternalSelectionChange,
  selectedFiles,
  enabledFileTypes,
  showMergeDocuments = true,
  showShareFolders = true,
  excludedFileIds,
  autoOpenNewFileUpload,
  afterCreateFile,
  useExternalSelection = false,
  showSelectionActions = true,
  enableTreeDrag = true,
  allowFileSelection = true,
  showCheckboxes = true,
  onSelectRoot,
  compactPreviewLayout = false,
  selectedFileId,
}: FileRepositoryProps) {
  const { openDuplication } = useFileDuplication();
  const matterId = matter?.id ?? "";
  const fileParentId = massTort?.id ?? matterId;
  const matterTitle = matter?.title.trim() || "Files";
  const matterFiles = useMemo(() => {
    const files = matter?.files ?? [];
    if (!Array.isArray(files)) return [];

    return files
      .map((file) => toRepoMatterFile(file, matterId))
      .filter((file): file is MatterFile => Boolean(file));
  }, [matter?.files, matterId]);
  const massTortFiles = useMemo(
    () =>
      (massTort?.files ?? [])
        .map((file) => toRepoMatterFile(file, fileParentId))
        .filter((file): file is MatterFile => Boolean(file)),
    [fileParentId, massTort?.files],
  );
  const matterContactConnections = useMemo(() => {
    return matter ? getMatterContactConnections(matter) : [];
  }, [matter]);
  const matterShareFolders = useMemo(() => {
    const folders = matter?.shareFolders ?? [];
    return Array.isArray(folders) ? folders : [];
  }, [matter?.shareFolders]);
  const matterShareFolderFiles = useMemo(() => {
    const files = matter?.shareFolderFiles ?? [];
    return Array.isArray(files) ? files : [];
  }, [matter?.shareFolderFiles]);

  const fileParentInput = massTort
    ? { massTortId: fileParentId }
    : { matterId: fileParentId };
  const fileParentTitle = massTort?.title?.trim() || matterTitle;
  const isMatterRepository = Boolean(matter);
  const canUseMatterOnlyFeatures = isMatterRepository && Boolean(matterId);
  const effectiveShowShareFolders =
    canUseMatterOnlyFeatures && showShareFolders;
  const { items: shareFolderItems, folders: shareFolderTreeFolders } =
    useShareFolderTreeData(matterId, effectiveShowShareFolders);

  const { data: contactsData } = useQuery({
    ...searchContactsQueryOptions({
      matterIds: matterId ? [matterId] : [],
      contactIds: uniq(
        matterContactConnections
          .map((connection) => connection.contactId)
          .filter((contactId): contactId is string => Boolean(contactId)),
      ),
    }),
    enabled: canUseMatterOnlyFeatures,
  });
  const contacts = useMemo(
    () => contactsData?.data ?? [],
    [contactsData?.data],
  );

  const { hasPagePermission, hasActionPermission } = useUser();

  const canViewShareFolders = effectiveShowShareFolders;

  const canCreateShareFolders =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterShareFolder,
      ACTION_PERMISSION_VALUES.create,
    ) && effectiveShowShareFolders;

  const canUpdateShareFolders =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterShareFolder,
      ACTION_PERMISSION_VALUES.update,
    ) && effectiveShowShareFolders;

  const canDeleteShareFolders =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterShareFolder,
      ACTION_PERMISSION_VALUES.delete,
    ) && effectiveShowShareFolders;
  const canCreateShareFolderFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterShareFolderFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteShareFolderFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterShareFolderFile,
    ACTION_PERMISSION_VALUES.delete,
  );

  const canUpdateFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canViewFiles = hasPagePermission(
    massTort
      ? PAGE_PERMISSION_KEYS.massTortsDetailFiles
      : PAGE_PERMISSION_KEYS.mattersDetailFiles,
  );
  const canEditDocxFiles =
    canUpdateFiles &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterFile,
      ACTION_PERMISSION_VALUES.update,
    );

  const [query, setQuery] = useState("");
  const [activeSidebarTab, setActiveSidebarTab] =
    useState<SidebarTab>("folders");
  const fileSearch = useFileSearch(
    query,
    massTort
      ? { scope: "mass_tort", massTortId: fileParentId }
      : { scope: "matter", matterId },
    canViewFiles && Boolean(fileParentId) && activeSidebarTab === "folders",
  );
  const regularItems = useMemo<RegularNode[]>(
    () =>
      mergeFileSearchResults(
        massTort ? massTortFiles : matterFiles,
        fileSearch.hits,
      )
        .filter((f) => !excludedFileIds || !excludedFileIds.has(f.id))
        .map((f) => ({
          ...f,
          matterId: f.matterId,
          kind: "regular",
        })),
    [excludedFileIds, massTort, massTortFiles, matterFiles, fileSearch.hits],
  );

  const shareFolderRepoItems = useMemo<ShareFolderNode[]>(
    () =>
      canViewShareFolders
        ? shareFolderItems.map(
            (file) => ({ ...file, kind: "share-folder" }) as ShareFolderNode,
          )
        : [],
    [canViewShareFolders, shareFolderItems],
  );

  const [sidebarState, setSidebarState] = useState<
    Record<
      SidebarTab,
      { currentFolderId: string; selectedItemId: string | null }
    >
  >({
    folders: { currentFolderId: "", selectedItemId: null },
    "shared-folders": { currentFolderId: "", selectedItemId: null },
  });
  const [selectedItemIdsByTab, setSelectedItemIdsByTab] = useState<
    Record<SidebarTab, Set<string>>
  >({
    folders: new Set(),
    "shared-folders": new Set(),
  });

  const currentFolderId = sidebarState[activeSidebarTab].currentFolderId;
  const selectedItemId = sidebarState[activeSidebarTab].selectedItemId;
  const internalSelectedItemIds = selectedItemIdsByTab[activeSidebarTab];
  const externalSelectedItemIds = useMemo(() => {
    if (!useExternalSelection) return undefined;
    return new Set((selectedFiles ?? []).map((file) => file.id));
  }, [selectedFiles, useExternalSelection]);
  const selectedItemIds = externalSelectedItemIds ?? internalSelectedItemIds;

  const setCurrentFolderId = (folderId: string) => {
    setSidebarState((prev) => ({
      ...prev,
      [activeSidebarTab]: {
        ...prev[activeSidebarTab],
        currentFolderId: folderId,
      },
    }));
  };

  const setSelectedItemId = useCallback(
    (itemId: string | null) => {
      setSidebarState((prev) => ({
        ...prev,
        [activeSidebarTab]: {
          ...prev[activeSidebarTab],
          selectedItemId: itemId,
        },
      }));
    },
    [activeSidebarTab],
  );

  const activeItems = useMemo<RepoItem[]>(() => {
    return activeSidebarTab === "folders" ? regularItems : shareFolderRepoItems;
  }, [activeSidebarTab, regularItems, shareFolderRepoItems]);

  const selectedItems = useMemo(
    () => activeItems.filter((item) => selectedItemIds.has(item.id)),
    [activeItems, selectedItemIds],
  );

  useEffect(() => {
    if (!canViewShareFolders && activeSidebarTab === "shared-folders") {
      setActiveSidebarTab("folders");
    }
  }, [activeSidebarTab, canViewShareFolders]);

  useEffect(() => {
    setSelectedItemIdsByTab((prev) => {
      const activeIds = new Set(activeItems.map((item) => item.id));
      const nextSet = new Set<string>();
      prev[activeSidebarTab].forEach((id) => {
        if (activeIds.has(id)) {
          nextSet.add(id);
        }
      });
      if (nextSet.size === prev[activeSidebarTab].size) {
        return prev;
      }
      return { ...prev, [activeSidebarTab]: nextSet };
    });
  }, [activeItems, activeSidebarTab]);

  const allItems = useMemo<RepoItem[]>(() => {
    return [...regularItems, ...shareFolderRepoItems];
  }, [regularItems, shareFolderRepoItems]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, RepoItem[]>();
    activeItems.forEach((item) => {
      const parentId = item.idLocation || "";
      const siblings = map.get(parentId) ?? [];
      siblings.push(item);
      map.set(parentId, siblings);
    });
    return map;
  }, [activeItems]);

  const isSelectableFile = useCallback(
    (item: RepoItem) => {
      if (item.isFolder) return false;
      if (!enabledFileTypes) return true;
      return enabledFileTypes.includes(
        getFileNameParts(item.name).fileExtension,
      );
    },
    [enabledFileTypes],
  );

  const selectFolders = !useExternalSelection && activeSidebarTab === "folders";

  const getDescendantSelectionIds = useCallback(
    (folderId: string) => {
      const ids = new Set<string>(selectFolders ? [folderId] : []);
      const stack = [folderId];
      while (stack.length > 0) {
        const currentId = stack.pop();
        if (!currentId) continue;
        const children = childrenByParent.get(currentId) ?? [];
        children.forEach((child) => {
          if (child.isFolder) {
            if (selectFolders) ids.add(child.id);
            stack.push(child.id);
          } else if (isSelectableFile(child)) {
            ids.add(child.id);
          }
        });
      }
      return Array.from(ids);
    },
    [childrenByParent, isSelectableFile, selectFolders],
  );

  const handleToggleSelection = useCallback(
    (items: RepoItem[], checked: boolean) => {
      const fileIds = [
        ...new Set(
          items.flatMap((item) =>
            item.isFolder
              ? getDescendantSelectionIds(item.id)
              : isSelectableFile(item)
                ? [item.id]
                : [],
          ),
        ),
      ];
      if (fileIds.length === 0) return;

      if (useExternalSelection && onExternalSelectionChange) {
        onExternalSelectionChange(fileIds, checked);
        return;
      }
      setSelectedItemIdsByTab((prev) => {
        const next = new Set(prev[activeSidebarTab]);
        fileIds.forEach((id) => {
          if (checked) {
            next.add(id);
          } else {
            next.delete(id);
            // A partially selected subtree must no longer select its whole parent.
            let parentId = activeItems.find(
              (item) => item.id === id,
            )?.idLocation;
            while (parentId && next.has(parentId)) {
              next.delete(parentId);
              parentId = activeItems.find(
                (item) => item.id === parentId,
              )?.idLocation;
            }
          }
        });
        return { ...prev, [activeSidebarTab]: next };
      });
    },
    [
      activeSidebarTab,
      activeItems,
      getDescendantSelectionIds,
      isSelectableFile,
      onExternalSelectionChange,
      useExternalSelection,
    ],
  );

  const handleToggleRootSelection = useCallback(
    (checked: boolean) => {
      if (useExternalSelection && onExternalSelectionChange) {
        const fileIds = activeItems
          .filter((item) => isSelectableFile(item))
          .map((item) => item.id);
        if (fileIds.length === 0) return;
        onExternalSelectionChange(fileIds, checked);
        return;
      }
      setSelectedItemIdsByTab((prev) => {
        const next = checked
          ? new Set(
              activeItems
                .filter(
                  (item) =>
                    (selectFolders && item.isFolder) || isSelectableFile(item),
                )
                .map((item) => item.id),
            )
          : new Set<string>();
        return { ...prev, [activeSidebarTab]: next };
      });
    },
    [
      activeItems,
      activeSidebarTab,
      selectFolders,
      isSelectableFile,
      onExternalSelectionChange,
      useExternalSelection,
    ],
  );

  const multiFileInputRef = useRef<HTMLInputElement>(null);
  const autoOpenHandledRef = useRef(false);
  const processedSelectedFileIdRef = useRef<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<RepoItem | undefined>();
  const [bulkItemsToDelete, setBulkItemsToDelete] = useState<RepoItem[] | null>(
    null,
  );
  const [isDeleteModalPending, setIsDeleteModalPending] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSelectDocumentModalOpen, setIsSelectDocumentModalOpen] =
    useState(false);
  const [isCreateDocxOpen, setIsCreateDocxOpen] = useState(false);
  const [docxEditorFile, setDocxEditorFile] = useState<MatterFile | null>(null);
  const [pdfEditorFile, setPdfEditorFile] = useState<MatterFile | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [versionControlsContainer, setVersionControlsContainer] =
    useState<HTMLDivElement | null>(null);
  const [previewVersion, setPreviewVersion] =
    useState<FilePreviewVersion | null>(null);
  const [sortKey] = useState<FileSortKey>("dateModified");
  const isDropEnabled = activeSidebarTab === "folders";

  const {
    openMergeDocumentModal,
    openGenerateDocumentModal,
    openFileModal,
    closeSlideover,
    openShareFolderModal,
    openShareFolderAccessModal,
  } = useGlobalModal();

  const createMatterFile = useCreateMatterFile();
  const deleteMatterFile = useDeleteMatterFile();
  const deleteMatterFileSilently = useDeleteMatterFile({ showToast: false });
  const updateMatterFile = useUpdateMatterFile();
  const moveMatterFiles = useUpdateMatterFile({ showToast: false });
  const deleteMatterShareFolderFile = useDeleteMatterShareFolderFile();
  const deleteMatterShareFolderFileSilently = useDeleteMatterShareFolderFile({
    showToast: false,
  });
  const deleteMatterShareFolder = useDeleteMatterShareFolder();
  const deleteMatterShareFolderSilently = useDeleteMatterShareFolder({
    showToast: false,
  });
  const createMatterShareFolderFile = useCreateMatterShareFolderFile();
  const isUploadingFiles = createMatterFile.isPending;

  // Shared-folder membership is local; regular repositories use backend search.
  const globallyFilteredItems = useMemo(() => {
    const sanitizedQuery = query.trim();
    if (!sanitizedQuery || activeSidebarTab !== "shared-folders") return null;
    const fuse = new Fuse(activeItems, {
      keys: [
        { name: "name", weight: 0.9 },
        { name: "description", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
    });
    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [activeItems, activeSidebarTab, query]);

  const showDropOverlay = isDropEnabled && (isDragActive || isUploadingFiles);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return undefined;
    return activeItems.find(
      (item) => item.id === selectedItemId || item.id === selectedItemId,
    );
  }, [activeItems, selectedItemId]);

  const selectedShareFolderId = useMemo(() => {
    if (activeSidebarTab !== "shared-folders") return undefined;
    if (!selectedItem?.isFolder) return undefined;
    return selectedItem.kind === "share-folder"
      ? selectedItem.shareFolderId
      : undefined;
  }, [activeSidebarTab, selectedItem]);

  const selectedShareFolder = useMemo(() => {
    if (!selectedShareFolderId) return undefined;
    return matterShareFolders.find(
      (folder) => folder.id === selectedShareFolderId,
    );
  }, [matterShareFolders, selectedShareFolderId]);

  // Get file IDs that are already in the selected share folder to exclude them from selection
  const selectedShareFolderExcludedFileIds = useMemo(() => {
    if (!selectedShareFolderId) return undefined;
    return new Set(
      matterShareFolderFiles
        .filter((file) => file.shareFolderId === selectedShareFolderId)
        .map((file) => file.fileId),
    );
  }, [matterShareFolderFiles, selectedShareFolderId]);

  // Sidebar tree filters to matched items plus their ancestor folders when searching
  const treeItems = useMemo(() => {
    if (!globallyFilteredItems) return activeItems;
    const byId = new Map(activeItems.map((i) => [i.id, i] as const));
    const include = new Map<string, RepoItem>();
    const addWithAncestors = (it: RepoItem) => {
      include.set(it.id, it);
      let parentId = it.idLocation;
      while (parentId) {
        const parent = byId.get(parentId);
        if (!parent) break;
        include.set(parent.id, parent);
        parentId = parent.idLocation;
      }
    };
    globallyFilteredItems.forEach(addWithAncestors);
    return Array.from(include.values());
  }, [activeItems, globallyFilteredItems]);

  // Navigate to a folder
  const navigateToFolder = (folderId: string) => {
    setCurrentFolderId(folderId);
    if (folderId === "") {
      setSelectedItemId(null);
    } else {
      setSelectedItemId(folderId); // Also select the folder when navigating to it
    }
  };

  // Select an item
  const handleSelectItem = (selected: RepoItem | string) => {
    const itemId = typeof selected === "string" ? selected : selected.id;

    // if root
    if (itemId === "") {
      onSelectRoot?.();
      setCurrentFolderId("");
      setSelectedItemId(null); // Also select the folder when navigating to it
      return;
    }

    setSelectedItemId(itemId);

    // Get the selected item
    const item = activeItems.find((item) => item.id === itemId);

    // If it's a folder, navigate into it
    if (item?.isFolder) {
      if (overRideFolderSelectFn) {
        overRideFolderSelectFn(itemId);
      }
      navigateToFolder(itemId);
    } else if (item) {
      if (!allowFileSelection) {
        return;
      }

      if (showCheckboxes && isSelectableFile(item)) {
        if (useExternalSelection && onExternalSelectionChange) {
          onExternalSelectionChange([item.id], true, true);
        } else {
          setSelectedItemIdsByTab((prev) => ({
            ...prev,
            [activeSidebarTab]: new Set([item.id]),
          }));
        }
      }

      // For files, use the same logic as grid/list views
      if (overRideFileSelectFn) {
        overRideFileSelectFn(itemId);
        return;
      }
      // Also navigate to the file's parent folder
      setCurrentFolderId(item.idLocation ?? "");
    }
  };

  useEffect(() => {
    if (!selectedFileId) {
      processedSelectedFileIdRef.current = null;
      return;
    }

    if (processedSelectedFileIdRef.current === selectedFileId) {
      return;
    }

    const regularMatch = regularItems.find(
      (item) => item.id === selectedFileId,
    );
    const shareMatch = canViewShareFolders
      ? shareFolderRepoItems.find((item) => item.id === selectedFileId)
      : undefined;
    const item = regularMatch ?? shareMatch;

    if (!item) {
      return;
    }

    processedSelectedFileIdRef.current = selectedFileId;

    const tab: SidebarTab = regularMatch ? "folders" : "shared-folders";
    const nextFolderId = item.isFolder ? item.id : item.idLocation;

    if (tab !== activeSidebarTab) {
      setActiveSidebarTab(tab);
    }

    setSidebarState((prev) => ({
      ...prev,
      [tab]: {
        ...prev[tab],
        currentFolderId: nextFolderId,
        selectedItemId: selectedFileId,
      },
    }));
  }, [
    activeSidebarTab,
    canViewShareFolders,
    regularItems,
    selectedFileId,
    shareFolderRepoItems,
  ]);

  // Open the create dialog
  const openCreateDialog = (
    isFolder: boolean,
    options?: { autoOpenUpload?: boolean },
  ) => {
    openFileModal({
      ...fileParentInput,
      editing: false,
      currentFolderId,
      isFolder,
      autoOpenUpload: options?.autoOpenUpload,
      afterCreateFile,
    });
  };

  useEffect(() => {
    if (!autoOpenNewFileUpload) {
      autoOpenHandledRef.current = false;
      return;
    }

    // Preventing bug where file select opens multiple times
    if (autoOpenHandledRef.current) return;
    autoOpenHandledRef.current = true;

    openCreateDialog(false, { autoOpenUpload: true });

    // Omiting openCreateDialog from deps to avoid re-opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNewFileUpload]);

  const openShareFolderDialog = () => {
    const initialSelectedFileIds = selectedFiles?.map(
      (fileItem) => fileItem.id,
    );

    const derivedSelection =
      initialSelectedFileIds && initialSelectedFileIds.length > 0
        ? initialSelectedFileIds
        : selectedItem && !selectedItem.isFolder
          ? [selectedItem.id]
          : [];

    openShareFolderModal({
      matterId,
      initialSelectedFileIds: derivedSelection,
    });
  };

  // Open the edit slideover
  const openEditSlideover = (item: RepoItem) => {
    if (item.kind === "share-folder") {
      return;
    }

    openFileModal({
      ...fileParentInput,
      activeFileId: item.id,
      editing: true,
      currentFolderId,
      isFolder: item.isFolder ?? false,
      handleDeleteItem: async () => {
        setItemToDelete(item);
        await new Promise((resolve) => setTimeout(resolve, 0));
      },
      showPreview: false,
    });
  };

  const handleMultiFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    Object.values(files).forEach((file) => {
      createMatterFile.mutate({
        ...fileParentInput,
        isFolder: false,
        idLocation: currentFolderId,
        name: file.name,
        file: file,
      });
    });
  };

  const dragContainsFiles = (event: DragEvent<HTMLDivElement>) => {
    const types = event.dataTransfer.types;
    return Array.from(types).includes("Files");
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDropEnabled) return;
    if (!dragContainsFiles(event)) return;
    setIsDragActive(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (!isDropEnabled) return;
    if (!dragContainsFiles(event)) return;
    setIsDragActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleDropFiles = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDropEnabled || !dragContainsFiles(event)) return;
    setIsDragActive(false);
    handleMultiFileUpload(event.dataTransfer.files);
  };

  const deleteRepoItem = async (
    item: RepoItem,
    { showToast = true }: { showToast?: boolean } = {},
  ) => {
    const deleteFile = showToast ? deleteMatterFile : deleteMatterFileSilently;
    const deleteShareFolder = showToast
      ? deleteMatterShareFolder
      : deleteMatterShareFolderSilently;
    const deleteShareFolderFile = showToast
      ? deleteMatterShareFolderFile
      : deleteMatterShareFolderFileSilently;

    if (item.kind === "share-folder") {
      const canDeleteShareFolderItem = item.isFolder
        ? canDeleteShareFolders
        : canDeleteShareFolderFiles;
      if (!canDeleteShareFolderItem) {
        toast.error("You do not have permission to delete this shared item");
        return;
      }

      if (!item.shareFolderId) {
        toast.error("Unable to determine share folder for this item");
        return;
      }

      // check if its a top level share folder
      if (item.isFolder) {
        await deleteShareFolder.mutateAsync({
          shareFolderId: item.shareFolderId,
          matterId: item.matterId,
        });
        return;
      }

      // Find the share folder to check if this is the last file
      const shareFolder = shareFolderTreeFolders.find(
        (folder) => folder.id === item.shareFolderId,
      );

      await deleteShareFolderFile.mutateAsync({
        matterId,
        id: item.fileId,
      });

      // If this was the last file in the share folder, delete the folder too
      if (shareFolder && shareFolder.files.length === 1) {
        await deleteShareFolder.mutateAsync({
          shareFolderId: item.shareFolderId,
          matterId,
        });
      }
      return;
    }

    if (!canDeleteFiles) {
      toast.error("You do not have permission to delete files");
      return;
    }

    await deleteFile.mutateAsync({
      ...fileParentInput,
      id: item.id,
    });
  };

  // Delete an item
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    await deleteRepoItem(itemToDelete);

    // If the deleted item was selected, clear selection
    if (selectedItemId === itemToDelete.id) {
      setSelectedItemId(null);
    }
  };

  // Handle saving files to share folder
  const handleSaveFilesToShareFolder = async (files: MatterFile[]) => {
    if (!canCreateShareFolderFiles) {
      toast.error("You do not have permission to add files to share folders");
      return;
    }
    if (!selectedShareFolderId) {
      throw new Error("No share folder selected");
    }

    // Get existing share folder file IDs to filter them out
    const existingShareFolderFileIds = new Set(
      matterShareFolderFiles
        .filter((file) => file.shareFolderId === selectedShareFolderId)
        .map((file) => file.fileId),
    );

    // Filter out files that are already in the share folder
    const newFiles = files.filter(
      (file) => !existingShareFolderFileIds.has(file.id),
    );

    if (newFiles.length === 0) {
      toast.error("All selected files are already in the share folder");
      return;
    }

    const results = await Promise.allSettled(
      newFiles.map((file) =>
        createMatterShareFolderFile.mutateAsync({
          matterId,
          shareFolderId: selectedShareFolderId,
          fileId: file.id,
        }),
      ),
    );

    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length > 0) {
      toast.error(
        `Failed to add ${failures.length} file(s) to the share folder`,
      );
    } else {
      toast.success(
        `Successfully added ${newFiles.length} file(s) to the share folder`,
      );
    }
  };

  // Handle moving an item to a new folder
  const handleMoveItem = async (item: MatterFile, targetFolderId: string) => {
    // Don't allow moving share-folder items
    const repoItem = allItems.find((i) => i.id === item.id);
    if (repoItem?.kind === "share-folder") {
      toast.error("Cannot move items in share folders", {
        id: "no-move-share-folder",
      });
      return false;
    }

    // Don't allow moving a folder into itself or its descendants
    if (!canUpdateFiles) {
      toast.error("You do not have permission to move items", {
        id: "no-permission-move-file",
      });
      return false;
    }
    if (item.id === targetFolderId) {
      toast.error("Cannot move a folder into itself");
      return false;
    }

    if (!item.id) return false;

    const currentLocation = item.idLocation;
    if (currentLocation === targetFolderId) {
      return false;
    }

    // If moving a folder, check that we're not moving it into one of its descendants
    if (item.isFolder) {
      let currentFolderId = targetFolderId;
      let isDescendant = false;

      // Check if target folder is a descendant of the folder being moved
      while (currentFolderId) {
        const folder = allItems.find((item) => item.id === currentFolderId);
        if (!folder) break;

        if (folder.id === item.id) {
          isDescendant = true;
          break;
        }

        currentFolderId = folder.idLocation ?? "";
      }

      if (isDescendant) {
        toast.error("Cannot move a folder into its descendants");
        return false;
      }
    }

    await updateMatterFile.mutateAsync({
      ...fileParentInput,
      id: item.id,
      idLocation: targetFolderId,
      dateModified: item.dateModified,
    });

    handleClearSelection();
    return true;
  };

  const handleMoveItems = async (items: RepoItem[], targetFolderId: string) => {
    const itemsToMove = getSelectedRepositoryRoots(items, regularItems).filter(
      (item) =>
        item.kind !== "share-folder" &&
        item.id &&
        (item.idLocation ?? "") !== targetFolderId,
    );
    if (itemsToMove.length === 0) return;

    const conflict = getFileMoveConflictMessage(
      itemsToMove,
      regularItems,
      targetFolderId,
    );
    if (conflict) {
      toast.error(conflict);
      return;
    }

    const results = await Promise.allSettled(
      itemsToMove.map((item) =>
        moveMatterFiles.mutateAsync({
          ...fileParentInput,
          id: item.id,
          idLocation: targetFolderId,
          dateModified: item.dateModified,
        }),
      ),
    );
    const movedCount = results.filter(
      (result) => result.status === "fulfilled",
    ).length;
    const failedCount = results.length - movedCount;

    if (failedCount > 0) {
      toast.error(
        `${movedCount} file${movedCount === 1 ? "" : "s"} moved; ${failedCount} failed`,
      );
      return;
    }

    toast.success(
      `${movedCount} file${movedCount === 1 ? "" : "s"} moved successfully`,
    );
    handleClearSelection();
  };

  const renderNewItemButtons = () => {
    return hasActionPermission(
      ACTION_PERMISSION_KEYS.matterFile,
      ACTION_PERMISSION_VALUES.create,
    ) && !selectedShareFolderId ? (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            openCreateDialog(true);
          }}
          type="button"
        >
          <Folder className="mr-2 h-4 w-4" />
          New folder
        </Button>
        <div className="flex items-center">
          <div className="flex">
            <Button
              variant="outline"
              size="sm"
              className="rounded-r-none border-r-0"
              onClick={() => {
                openCreateDialog(false, { autoOpenUpload: true });
              }}
              type="button"
            >
              <File className="mr-2 h-4 w-4" />
              Upload file
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="upload-file-dropdown-button"
                  variant="outline"
                  className="rounded-l-none px-2"
                  size="sm"
                  title="Upload options"
                  type="button"
                >
                  {createMatterFile.isPending ? (
                    <LoadingSpinner />
                  ) : (
                    <EllipsisVertical className="h-4 w-4" />
                  )}
                  <span className="sr-only">Upload options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canUseMatterOnlyFeatures ? (
                  <DropdownMenuItem
                    onClick={() => {
                      setIsCreateDocxOpen(true);
                    }}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Create file
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  data-testid="bulk-upload-dropdown-item"
                  onClick={() => {
                    // Reset value to ensure onChange fires even if same files are re-selected
                    if (multiFileInputRef.current) {
                      multiFileInputRef.current.value = "";
                      multiFileInputRef.current.click();
                    }
                  }}
                >
                  <FilesIcon className="mr-2 h-4 w-4" />
                  Bulk upload
                </DropdownMenuItem>
                {matter ? (
                  <DropdownMenuItem
                    data-testid="generate-document-dropdown-item"
                    onClick={() => {
                      openGenerateDocumentModal({
                        matter,
                        contacts,
                        matterFileIdLocation: currentFolderId,
                      });
                    }}
                  >
                    <WandSparkles className="mr-2 h-4 w-4" />
                    Generate document
                  </DropdownMenuItem>
                ) : null}
                {canCreateShareFolders && (
                  <DropdownMenuItem
                    data-testid="new-share-folder-dropdown-item"
                    onClick={openShareFolderDialog}
                  >
                    <Share2 className="mr-2 h-4 w-4" />
                    New share folder
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ) : null;
  };

  const renderShareFolderButtons = () => {
    return canCreateShareFolders ? (
      <Button
        variant="outline"
        size="sm"
        onClick={openShareFolderDialog}
        type="button"
        disabled={!canCreateShareFolders}
      >
        <Share2 className="mr-2 h-4 w-4" />
        New share folder
      </Button>
    ) : null;
  };

  const isShareFolderView = activeSidebarTab === "shared-folders";
  const isShareFolderRoot = isShareFolderView && !selectedShareFolderId;
  const canUploadToFolder =
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterFile,
      ACTION_PERMISSION_VALUES.create,
    ) && !selectedShareFolderId;

  const previewPresignedKey =
    selectedItem && !selectedItem.isFolder
      ? selectedItem.kind === "share-folder"
        ? selectedItem.presignedUrlKey || selectedItem.presignedUrlKey
        : selectedItem.presignedUrlKey
      : undefined;

  const previewFileType =
    selectedItem?.kind === "share-folder"
      ? "matter-share-folder-file"
      : "matter-file";

  const previewFile =
    selectedItem &&
    !selectedItem.isFolder &&
    (previewPresignedKey ||
      (selectedItem.kind !== "share-folder" && canDownloadFile(selectedItem)))
      ? {
          fileName: selectedItem.name,
          fileId:
            selectedItem.kind !== "share-folder" ? selectedItem.id : undefined,
          presignedURLKey: previewPresignedKey || "",
        }
      : undefined;
  const isSelectedDocx =
    !!selectedItem &&
    !selectedItem.isFolder &&
    getFileDocumentFormat(selectedItem) === "docx";

  const isSelectedPdf =
    !!selectedItem &&
    !selectedItem.isFolder &&
    getFileDocumentFormat(selectedItem) === "pdf";

  const canEditSelectedDocx =
    isSelectedDocx && selectedItem.kind !== "share-folder" && canEditDocxFiles;

  const canEditSelectedPdf =
    isSelectedPdf && selectedItem.kind !== "share-folder" && canEditDocxFiles;

  const handleOpenDocxEditor = () => {
    if (!selectedItem || selectedItem.kind === "share-folder") return;
    if (!isSelectedDocx || !canEditSelectedDocx) return;
    setDocxEditorFile(selectedItem);
  };

  const handleOpenPdfEditor = () => {
    if (!selectedItem || selectedItem.kind === "share-folder") return;
    if (!isSelectedPdf || !canEditSelectedPdf) return;
    setPdfEditorFile(selectedItem);
  };

  const showPreview = !!selectedItem && !selectedItem.isFolder;
  const showUploadCta = !isShareFolderView;
  const showShareFolderEmpty = isShareFolderView;
  const uploadTargetLabel =
    selectedItem?.name ||
    (activeSidebarTab === "folders" ? fileParentTitle : "Shared folders");

  const headerTitle = query.trim()
    ? "Search results"
    : selectedItem?.name ||
      (activeSidebarTab === "folders" ? fileParentTitle : "Shared folders");
  const showMatterHeaderPreview =
    Boolean(matter) &&
    !query.trim() &&
    !selectedItem &&
    activeSidebarTab === "folders";

  const HeaderIcon = selectedItem
    ? selectedItem.isFolder
      ? selectedItem.kind === "share-folder"
        ? FolderInput
        : Folder
      : getFileIcon(selectedItem.name)
    : Folder;

  const canEditSelectedItem =
    !!selectedItem && selectedItem.kind !== "share-folder";

  const canDeleteSelectedItem =
    !!selectedItem &&
    (selectedItem.kind === "share-folder"
      ? selectedItem.isFolder
        ? canDeleteShareFolders
        : canDeleteShareFolderFiles
      : canDeleteFiles);

  const canDownloadSelectedItem =
    !!selectedItem &&
    !selectedItem.isFolder &&
    Boolean(
      selectedItem.kind === "share-folder"
        ? selectedItem.presignedUrlKey || selectedItem.presignedUrlKey
        : canDownloadFile(selectedItem),
    );

  const canShareSelectedItem = selectedShareFolderId
    ? canUpdateShareFolders
    : canCreateShareFolders;
  const showShareSelectedItemButton =
    effectiveShowShareFolders && canShareSelectedItem;

  const showSelectionStatus = showSelectionActions && selectedItems.length > 0;

  const handleDownloadSelectedItem = async () => {
    if (!selectedItem || selectedItem.isFolder) return;
    try {
      if (selectedItem.kind !== "share-folder") {
        const versionId =
          previewVersion?.fileId === selectedItem.id
            ? previewVersion.versionId
            : null;
        const result = versionId
          ? await downloadFileVersion(selectedItem.id, versionId)
          : await downloadFile(selectedItem.id);
        downloadFileLink(result.fileName, result.url);
        return;
      }
      const presignedKey = selectedItem.presignedUrlKey;
      if (!presignedKey) {
        toast.error("This file is not available to download.");
        return;
      }
      const presigned = await generateS3ItemPreSignedUrlV1(
        "matter-share-folder-file",
      )({
        clientMethod: "get_object",
        key: presignedKey,
      });
      await handleDownload(selectedItem.name, presigned.data.presignedUrl);
    } catch (error) {
      console.error("Failed to download file", error);
      toast.error("Unable to download this file. Please try again.");
    }
  };

  const handleShareAction = () => {
    if (!matter) return;
    if (selectedShareFolderId && selectedShareFolder) {
      openShareFolderAccessModal({
        matterId,
        shareFolderId: selectedShareFolderId,
        title: selectedShareFolder.label
          ? `Share folder access – ${selectedShareFolder.label}`
          : "Share folder access",
        description: selectedShareFolder.label
          ? `Send shared folder access details for ${selectedShareFolder.label}.`
          : "Send shared folder access information.",
      });
      return;
    }
    if (
      selectedItem &&
      !selectedItem.isFolder &&
      selectedItem.kind !== "share-folder"
    ) {
      openShareFolderModal({
        matterId,
        initialSelectedFileIds: [selectedItem.id],
      });
      return;
    }
    if (canCreateShareFolders) {
      openShareFolderDialog();
    }
  };

  const handleDownloadItems = async (items: RepoItem[]) => {
    if (isDownloading) return;
    setIsDownloading(true);
    const toastId = toast.loading("Preparing download…");
    try {
      await downloadRepositoryItems(items, allItems);
    } catch (error) {
      console.error("Failed to download selected files", error);
      toast.error("Unable to download selected files. Please try again.");
    } finally {
      toast.dismiss(toastId);
      setIsDownloading(false);
    }
  };

  const handleShareItems = (items: RepoItem[]) => {
    if (!matter) return;
    const regularFileIds = items
      .filter((item) => item.kind !== "share-folder")
      .filter((item) => !item.isFolder)
      .map((item) => item.id);
    if (regularFileIds.length === 0) {
      toast.error("Select at least one file to share.");
      return;
    }
    openShareFolderModal({
      matterId,
      initialSelectedFileIds: regularFileIds,
    });
  };

  const handleMergeItems = (items: RepoItem[]) => {
    const mergeableFileIds = items
      .filter((item) => !item.isFolder && item.kind !== "share-folder")
      .filter((item) => isMergeSupportedMatterFile(item))
      .map((item) => item.id);
    const hasUnsupportedItems = items.some(
      (item) => !item.isFolder && !isMergeSupportedMatterFile(item),
    );

    if (
      !showMergeDocuments ||
      activeSidebarTab !== "folders" ||
      hasUnsupportedItems ||
      mergeableFileIds.length < 2
    ) {
      return;
    }

    if (matter) {
      openMergeDocumentModal({
        matterId,
        initialSelectedFileIds: mergeableFileIds,
      });
      return;
    }

    if (massTort) {
      const massTortId = massTort.id.trim();
      if (!massTortId) return;
      openMergeDocumentModal({
        massTortId,
        initialSelectedFileIds: mergeableFileIds,
      });
    }
  };

  const handleContextDelete = (selected: RepoItem[]) => {
    const items = getSelectedRepositoryRoots(selected, activeItems);
    if (items.length === 1) {
      setItemToDelete(items[0]);
      return;
    }
    setBulkItemsToDelete(items);
  };

  const handleConfirmItemDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleteModalPending(true);
    try {
      closeSlideover("file");
      await handleDeleteItem();
      setItemToDelete(undefined);
    } finally {
      setIsDeleteModalPending(false);
    }
  };

  const handleClearSelection = useCallback(() => {
    if (useExternalSelection && onExternalSelectionChange) {
      onExternalSelectionChange(Array.from(selectedItemIds), false);
      setSelectedItemId(null);
      return;
    }
    setSelectedItemIdsByTab((prev) => ({
      ...prev,
      [activeSidebarTab]: new Set(),
    }));
    setSelectedItemId(null);
  }, [
    activeSidebarTab,
    onExternalSelectionChange,
    selectedItemIds,
    setSelectedItemId,
    useExternalSelection,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (selectedItemIds.size === 0) return;
      handleClearSelection();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClearSelection, selectedItemIds]);

  const handleConfirmBulkDelete = async () => {
    if (!bulkItemsToDelete || bulkItemsToDelete.length === 0) return;
    const items = bulkItemsToDelete;
    setIsDeleteModalPending(true);
    try {
      const results = await Promise.allSettled(
        items.map((item) => deleteRepoItem(item, { showToast: false })),
      );
      const failures = results.filter((result) => result.status === "rejected");
      if (failures.length > 0) {
        toast.error(`Failed to delete ${failures.length} item(s).`);
      } else {
        toast.success(
          `Deleted ${items.length} ${items.length === 1 ? "file" : "files"}.`,
        );
      }
    } finally {
      setBulkItemsToDelete(null);
      setIsDeleteModalPending(false);
    }

    setSelectedItemIdsByTab((prev) => {
      const next = new Set(prev[activeSidebarTab]);
      items.forEach((item) => next.delete(item.id));
      return { ...prev, [activeSidebarTab]: next };
    });

    if (selectedItemId && items.some((item) => item.id === selectedItemId)) {
      setSelectedItemId(null);
    }
  };

  const renderItemContextMenu = (item: RepoItem, trigger: ReactNode) => {
    const itemsToActOn = selectedItemIds.has(item.id) ? selectedItems : [item];
    const duplicationRoots = getSelectedRepositoryRoots(
      itemsToActOn,
      regularItems,
    );
    const hasUnsupportedMergeItems = itemsToActOn.some(
      (candidate) =>
        !candidate.isFolder && !isMergeSupportedMatterFile(candidate),
    );
    const canMergeItems =
      Boolean(matter ?? massTort) &&
      activeSidebarTab === "folders" &&
      showMergeDocuments &&
      itemsToActOn.filter(
        (candidate) => !candidate.isFolder && candidate.kind !== "share-folder",
      ).length >= 2 &&
      !hasUnsupportedMergeItems;
    const canDeleteItems = itemsToActOn.every((candidate) =>
      candidate.kind === "share-folder"
        ? candidate.isFolder
          ? canDeleteShareFolders
          : canDeleteShareFolderFiles
        : canDeleteFiles,
    );
    const selectableDestinationFolders = regularItems.filter(
      (candidate) =>
        candidate.isFolder &&
        !itemsToActOn.some(
          (selectedItem) => selectedItem.idLocation === candidate.id,
        ) &&
        !itemsToActOn.some((selectedItem) => {
          if (selectedItem.id === candidate.id) return true;

          let parentId = candidate.idLocation ?? "";
          while (parentId) {
            if (parentId === selectedItem.id) return true;
            parentId =
              regularItems.find((folder) => folder.id === parentId)
                ?.idLocation ?? "";
          }

          return false;
        }),
    );
    const selectableDestinationFolderIds = new Set(
      selectableDestinationFolders.map((folder) => folder.id),
    );
    const folderById = new Map(
      regularItems
        .filter((candidate) => candidate.isFolder)
        .map((folder) => [folder.id, folder]),
    );
    const visibleDestinationFolderIds = new Set<string>();

    selectableDestinationFolders.forEach((folder) => {
      let currentFolder: RepoItem | undefined = folder;
      while (currentFolder) {
        visibleDestinationFolderIds.add(currentFolder.id);
        currentFolder = folderById.get(currentFolder.idLocation ?? "");
      }
    });

    const destinationFolders = regularItems
      .filter(
        (candidate) =>
          candidate.isFolder && visibleDestinationFolderIds.has(candidate.id),
      )
      .map((folder) => ({
        canMoveHere: selectableDestinationFolderIds.has(folder.id),
        folder,
      }));
    const canMoveItems =
      canUpdateFiles &&
      activeSidebarTab === "folders" &&
      itemsToActOn.some((candidate) => candidate.kind !== "share-folder");

    return (
      <MatterFileContextMenu
        canDelete={canDeleteItems}
        canDownload={canViewFiles}
        isDownloading={isDownloading}
        canEdit={
          canUpdateFiles &&
          itemsToActOn.length === 1 &&
          item.kind !== "share-folder"
        }
        canMerge={canMergeItems}
        canMove={canMoveItems}
        canDuplicate={
          canCreateFiles &&
          activeSidebarTab === "folders" &&
          duplicationRoots.every(
            (candidate) =>
              candidate.kind !== "share-folder" &&
              canOfferFileDuplication(candidate),
          )
        }
        canShare={effectiveShowShareFolders && canCreateShareFolders}
        duplicateItems={duplicationRoots}
        destinationFolders={destinationFolders}
        items={itemsToActOn}
        matter={matter}
        onDelete={() => {
          handleContextDelete(itemsToActOn);
        }}
        onDownload={() => {
          void handleDownloadItems(itemsToActOn);
        }}
        onEdit={() => {
          openEditSlideover(item);
        }}
        onMerge={() => {
          handleMergeItems(itemsToActOn);
        }}
        onCreateFolder={() => {
          openCreateDialog(true);
        }}
        onMove={(folderId) => {
          void handleMoveItems(itemsToActOn, folderId);
        }}
        onDuplicate={() => {
          openDuplication({
            files: duplicationRoots.map((candidate) => ({
              id: candidate.id,
              name: candidate.name,
              isFolder: !!candidate.isFolder,
            })),
            repository: massTort
              ? { scope: "mass_tort", massTortId: massTort.id }
              : { scope: "matter", matterId },
            label:
              duplicationRoots.length === 1
                ? duplicationRoots[0].name
                : `${duplicationRoots.length} items`,
          });
        }}
        rootFolderName={fileParentTitle}
        showRootFolder={itemsToActOn.every((selectedItem) =>
          Boolean(selectedItem.idLocation),
        )}
        onShare={() => {
          handleShareItems(itemsToActOn);
        }}
        trigger={trigger}
      />
    );
  };

  return (
    <div className="bg-card flex h-full w-full overflow-hidden rounded-none border">
      <div className="border-border bg-card flex w-[360px] max-w-[360px] min-w-[360px] shrink-0 flex-col border-r xl:w-[420px] xl:max-w-[420px] xl:min-w-[420px]">
        <div className="border-b">
          {canViewShareFolders && (
            <div className="px-4 py-4">
              <Tabs
                value={activeSidebarTab}
                onValueChange={(value) => {
                  if (value !== "folders" && value !== "shared-folders") {
                    return;
                  }
                  setActiveSidebarTab(value);
                }}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="folders">
                    <Text as="span" variant="body" className="text-current">
                      Folders
                    </Text>
                  </TabsTrigger>
                  <TabsTrigger value="shared-folders">
                    <Text as="span" variant="body" className="text-current">
                      Shared folders
                    </Text>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
          <div className={cn("px-4 pb-3", !canViewShareFolders && "pt-4")}>
            <div className="mt-3">
              {activeSidebarTab === "folders"
                ? renderNewItemButtons()
                : renderShareFolderButtons()}
            </div>
            <div className="mt-3">
              <Input
                value={query}
                maxLength={activeSidebarTab === "folders" ? 200 : undefined}
                aria-label={
                  activeSidebarTab === "folders"
                    ? "Search files"
                    : "Search shared folders"
                }
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                placeholder={
                  activeSidebarTab === "folders"
                    ? "Search files..."
                    : "Search shared folders..."
                }
                className="w-full"
              />
            </div>
          </div>
        </div>
        <ScrollHint
          showOnTop
          className="min-h-0 flex-1"
          contentClassName="h-full min-h-0 overflow-y-auto px-2 py-2"
          fadeClassName="from-black/20 to-transparent"
          hintClassName="hidden"
        >
          {activeSidebarTab === "folders" && query.trim() ? (
            <FileSearchResults
              search={fileSearch}
              files={regularItems}
              selectedFileId={selectedItemId}
              onSelect={handleSelectItem}
              selectedIds={selectedItemIds}
              onToggleSelection={
                showCheckboxes
                  ? (item, checked) => {
                      handleToggleSelection([item], checked);
                    }
                  : undefined
              }
              isSelectable={(item) =>
                allowFileSelection && isSelectableFile(item)
              }
              renderContextMenu={renderItemContextMenu}
            />
          ) : (
            <FolderTree
              matter={{ matterTitle: fileParentTitle }}
              items={treeItems}
              selectionItems={activeItems}
              showCheckboxes={showCheckboxes}
              selectedItemIds={selectedItemIds}
              selectedDragItems={selectedItems}
              selectionMode={selectFolders ? "all" : "files-only"}
              onToggleSelection={handleToggleSelection}
              onToggleRootSelection={handleToggleRootSelection}
              onSelectItem={handleSelectItem}
              selectedItemId={selectedItemId}
              onMoveItem={handleMoveItem}
              onMoveItems={handleMoveItems}
              enableDrag={enableTreeDrag}
              onNavigateToFolder={(folderId) => {
                setCurrentFolderId(folderId);
              }}
              onCreateFolder={
                activeSidebarTab === "folders"
                  ? () => {
                      openCreateDialog(true);
                    }
                  : undefined
              }
              currentFolderId={currentFolderId}
              enabledFileTypes={enabledFileTypes}
              isSearching={Boolean(query.trim())}
              sortKey={sortKey}
              showHeader={activeSidebarTab === "folders"}
              renderContextMenu={renderItemContextMenu}
            />
          )}
        </ScrollHint>
        {showSelectionStatus && (
          <div className="border-t px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FilesIcon className="text-muted-foreground/60 h-7 w-7" />
                <Text variant="body" className="font-semibold">
                  {selectedItems.length} selected
                </Text>
              </div>
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={handleClearSelection}
                title="Clear selection"
                className="h-7 w-7"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      <div
        className="bg-card relative flex h-full min-w-0 flex-1 flex-col"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDropFiles}
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex min-w-0 items-center gap-2">
            <HeaderIcon
              className={`h-5 w-5 ${
                selectedItem?.isFolder || !selectedItem
                  ? "text-amber-500"
                  : "text-blue-500"
              }`}
            />
            {showMatterHeaderPreview && matter ? (
              <MatterPreviewHoverCard
                matterId={matterId}
                previewData={{
                  matterId,
                  matterTitle,
                }}
              >
                <div className="min-w-0">
                  <Text
                    variant="subHeading"
                    color="foreground"
                    className="mb-0 block max-w-[520px] truncate text-nowrap break-normal"
                  >
                    {headerTitle}
                  </Text>
                </div>
              </MatterPreviewHoverCard>
            ) : (
              <FileVersionTitle setContainer={setVersionControlsContainer}>
                <Text
                  variant="subHeading"
                  color="foreground"
                  className="mb-0 block max-w-[520px] truncate text-nowrap break-normal"
                >
                  {headerTitle}
                </Text>
              </FileVersionTitle>
            )}

            <Button
              variant="ghost"
              size="icon"
              type="button"
              disabled={!canEditSelectedItem}
              onClick={() => {
                if (selectedItem && canEditSelectedItem) {
                  openEditSlideover(selectedItem);
                }
              }}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-1">
            {selectedShareFolderId && canCreateShareFolderFiles && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setIsSelectDocumentModalOpen(true);
                }}
              >
                <FilesIcon className="mr-2 h-4 w-4" />
                Add files
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              type="button"
              disabled={!canDownloadSelectedItem}
              onClick={handleDownloadSelectedItem}
              title="Download"
            >
              <Download className="h-4 w-4" />
            </Button>
            {effectiveShowShareFolders ? (
              <Button
                variant="ghost"
                size="icon"
                type="button"
                disabled={!showShareSelectedItemButton}
                onClick={handleShareAction}
                title="Share"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              type="button"
              disabled={!canDeleteSelectedItem}
              onClick={() => {
                if (selectedItem) {
                  setItemToDelete(selectedItem);
                }
              }}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex h-full flex-col overflow-hidden">
          {showPreview ? (
            previewFile ? (
              <div className={cn("flex h-full min-h-0 flex-col p-6")}>
                {isSelectedDocx && canEditSelectedDocx ? (
                  <FileViewer
                    file={previewFile}
                    onVersionChange={setPreviewVersion}
                    versionControlsContainer={versionControlsContainer}
                    fileType={previewFileType}
                    showDownload={false}
                    showTitle={false}
                    footerClassName="hidden"
                    viewerClassName={cn(
                      "items-stretch justify-stretch",
                      "bg-card",
                    )}
                    wrapperClassName="bg-card shadow-none rounded-none"
                    compactDocxPreview={compactPreviewLayout}
                    showDocxModeToggle
                    canEditDocx={canEditSelectedDocx}
                    onDocxEdit={handleOpenDocxEditor}
                  />
                ) : isSelectedPdf && canEditSelectedPdf ? (
                  <FileViewer
                    file={previewFile}
                    onVersionChange={setPreviewVersion}
                    versionControlsContainer={versionControlsContainer}
                    fileType={previewFileType}
                    showDownload={false}
                    showTitle={false}
                    footerClassName="hidden"
                    viewerClassName={cn(
                      "items-stretch justify-stretch",
                      "bg-card",
                    )}
                    wrapperClassName="bg-card shadow-none rounded-none"
                    showEditOverlay
                    canEditOverlay={canEditSelectedPdf}
                    onEdit={handleOpenPdfEditor}
                  />
                ) : (
                  <FileViewer
                    file={previewFile}
                    onVersionChange={setPreviewVersion}
                    versionControlsContainer={versionControlsContainer}
                    fileType={previewFileType}
                    showDownload={false}
                    showTitle={false}
                    footerClassName="hidden"
                    viewerClassName={cn(
                      "items-stretch justify-stretch",
                      "bg-card",
                    )}
                    wrapperClassName="bg-card shadow-none rounded-none"
                    compactDocxPreview={compactPreviewLayout}
                    showDocxModeToggle={isSelectedDocx}
                    canEditDocx={canEditSelectedDocx}
                    onDocxEdit={handleOpenDocxEditor}
                    showEditOverlay={isSelectedPdf}
                    canEditOverlay={canEditSelectedPdf}
                    onEdit={
                      canEditSelectedPdf ? handleOpenPdfEditor : undefined
                    }
                  />
                )}
              </div>
            ) : (
              <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
                <Text variant="body" className="font-semibold">
                  Preview not available.
                </Text>
                <Text variant="subHeading">
                  This file does not have a preview link.
                </Text>
              </div>
            )
          ) : (
            <div className="flex h-full flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              {showShareFolderEmpty ? (
                <>
                  <Text variant="body" className="font-semibold">
                    {isShareFolderRoot
                      ? "No shared folders yet."
                      : "No files in this shared folder."}
                  </Text>
                  <Text variant="subHeading">
                    {isShareFolderRoot
                      ? "Create a shared folder to get started."
                      : 'Use "Add files" to include files here.'}
                  </Text>
                </>
              ) : showUploadCta ? (
                <>
                  <button
                    type="button"
                    className={`group flex flex-col items-center gap-3 rounded-lg px-8 py-6 transition ${
                      canUploadToFolder
                        ? "hover:bg-muted/30 hover:shadow-sm"
                        : "cursor-not-allowed opacity-50"
                    }`}
                    disabled={!canUploadToFolder}
                    onClick={() => {
                      openCreateDialog(false, { autoOpenUpload: true });
                    }}
                  >
                    <div className="relative">
                      <Folder className="text-muted-foreground/40 h-16 w-16" />
                      <div className="bg-primary text-primary-foreground absolute -right-1 -bottom-1 flex h-7 w-7 items-center justify-center rounded-full">
                        <Plus className="h-4 w-4" />
                      </div>
                    </div>
                    <Text variant="body" className="font-semibold">
                      {`Add file to ${uploadTargetLabel}`}
                    </Text>
                    <Text variant="subHeading">Click to upload a file</Text>
                  </button>
                </>
              ) : (
                <>
                  <Text variant="body" className="font-semibold">
                    Select a file to preview.
                  </Text>
                  <Text variant="subHeading">
                    Choose a file from the left panel.
                  </Text>
                </>
              )}
            </div>
          )}
        </div>

        {showDropOverlay && (
          <div className="border-primary/70 bg-background/70 text-primary/90 pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 border-2 border-dashed backdrop-blur-sm">
            {isUploadingFiles ? (
              <>
                <LoadingSpinner className="text-primary h-6 w-6" />
                <Text variant="body" className="font-semibold">
                  Uploading files...
                </Text>
                <Text variant="subHeading">This may take a moment.</Text>
              </>
            ) : (
              <>
                <FilesIcon className="text-primary h-8 w-8" />
                <Text variant="body" className="font-semibold">
                  Drop files to upload
                </Text>
                <Text variant="subHeading">
                  Release to import into this folder.
                </Text>
              </>
            )}
          </div>
        )}
      </div>

      {matter ? (
        <SelectDocumentModal
          open={isSelectDocumentModalOpen}
          setOpen={setIsSelectDocumentModalOpen}
          matter={matter}
          onDocumentSelected={() => {}}
          multiple
          maxSelection={10}
          onSave={handleSaveFilesToShareFolder}
          excludedFileIds={selectedShareFolderExcludedFileIds}
        />
      ) : null}
      {matter ? (
        <CreateDocxFileSlideover
          open={isCreateDocxOpen}
          setOpen={setIsCreateDocxOpen}
          matterId={matterId}
          currentFolderId={currentFolderId}
          afterCreateFile={afterCreateFile}
        />
      ) : null}
      {docxEditorFile && matter ? (
        <EditDocxFileSlideover
          open={!!docxEditorFile}
          setOpen={(nextOpen) => {
            if (!nextOpen) {
              setDocxEditorFile(null);
            }
          }}
          matterId={matterId}
          sourceFile={docxEditorFile}
        />
      ) : null}
      {pdfEditorFile && matter && (
        <FileRepositoryPdfEditorSlideover
          open={!!pdfEditorFile}
          setOpen={(nextOpen) => {
            if (!nextOpen) {
              setPdfEditorFile(null);
            }
          }}
          matterId={matterId}
          sourceFile={pdfEditorFile}
        />
      )}
      <ActionConfirmationModal
        open={!!itemToDelete}
        setOpen={() => {
          setItemToDelete(undefined);
        }}
        title={`Delete ${itemToDelete?.isFolder ? "folder" : "file"}`}
        description={
          itemToDelete?.isFolder
            ? "Are you sure you want to delete this folder? If this folder has contents, they will be deleted as well. This action cannot be undone."
            : "Are you sure you want to delete this file? This action cannot be undone."
        }
        afterActionFn={handleConfirmItemDelete}
        isActionPending={isDeleteModalPending}
      />
      <ActionConfirmationModal
        open={!!bulkItemsToDelete}
        setOpen={() => {
          setBulkItemsToDelete(null);
        }}
        title={`Delete ${bulkItemsToDelete?.length ?? 0} item(s)`}
        description="Are you sure you want to delete the selected items? This action cannot be undone."
        afterActionFn={handleConfirmBulkDelete}
        isActionPending={isDeleteModalPending}
      />
      <input
        type="file"
        ref={multiFileInputRef}
        className="hidden"
        multiple
        onChange={(e) => {
          handleMultiFileUpload(e.target.files);
          e.currentTarget.value = "";
        }}
        accept={acceptedFileMimeTypes.join(",")}
      />
    </div>
  );
}
